import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Binoculars, BookOpen, Camera, Flower, GlobeHemisphereWest, HandHeart,
  Leaf, MapPin, Plant, SealCheck, Sparkle, Tree, Users,
} from "@phosphor-icons/react";
import { http, API_BASE } from "@/lib/api";
import SeoMeta from "@/components/SeoMeta";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import PageGuide from "@/components/PageGuide";
import RelatedReading from "@/components/RelatedReading";

function abs(u) { return !u ? "" : u.startsWith("http") ? u : `${API_BASE}${u}`; }

/* Mockup category circles — Trees routes to the stocked flora catalogue hub,
 * the rest run universal search until dedicated landing pages accumulate. */
const CATEGORIES = [
  { label: "Trees", art: "circle-trees", to: "/explore/flora", guide: "plant-guide-trees" },
  { label: "Flowers", art: "circle-flowers", to: "/search?q=flower", guide: "plant-guide-flowers" },
  { label: "Shrubs", art: "circle-shrubs", to: "/search?q=shrub", guide: "plant-guide-shrubs" },
  { label: "Grasses", art: "circle-grasses", to: "/search?q=grass", guide: "plant-guide-grasses" },
  { label: "Ferns", art: "circle-ferns", to: "/search?q=fern", guide: "plant-guide-ferns" },
  { label: "Wild Plants", art: "circle-wild", to: "/search?q=wildflower", guide: "plant-guide-wild-plants" },
  { label: "Medicinal Plants", art: "circle-medicinal", to: "/search?q=medicinal herb", guide: "plant-guide-medicinal-plants" },
  { label: "Crops & Food Plants", art: "circle-crops", to: "/search?q=crop", guide: "plant-guide-crops-food-plants" },
  { label: "Garden Plants", art: "circle-garden", to: "/search?q=garden plant", guide: "plant-guide-garden-plants" },
  { label: "Aquatic Plants", art: "circle-aquatic", to: "/search?q=aquatic plant", guide: "plant-guide-aquatic-plants" },
];

const FEATURES = [
  { icon: Leaf, title: "Identify Plants", copy: "Use AI and global databases to discover plant species.", guide: "plant-feature-identify-plants" },
  { icon: MapPin, title: "Explore Near You", copy: "See what's growing in your area and around the world.", guide: "plant-feature-explore-near-you" },
  { icon: BookOpen, title: "Learn & Grow", copy: "Get plant guides, care tips and fascinating plant facts.", guide: "plant-feature-learn-and-grow" },
  { icon: Binoculars, title: "Contribute", copy: "Share your observations and help build a global record.", guide: "plant-feature-contribute" },
  { icon: Users, title: "Protect Nature", copy: "Together we can support biodiversity and a healthier planet.", guide: "plant-feature-protect-nature" },
];

const LADDER = [
  ["AI suggested", "Visual AI proposes candidates — never a verdict"],
  ["Probable", "High-confidence match, not yet database-checked"],
  ["Database matched", "Validated against open taxonomic records"],
  ["Community confirmed", "Other observers saw and confirmed it"],
  ["Expert verified", "A qualified steward signed off"],
];

export default function PlantPulseHome() {
  const [nearby, setNearby] = useState(null); // null = not tried, [] = none
  const [locState, setLocState] = useState("idle"); // idle | locating | denied
  const [flora, setFlora] = useState(null);
  const [recent, setRecent] = useState([]);
  const [art, setArt] = useState({});
  const fileRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    http.get("/catalogue/flora").then(({ data }) => setFlora(data.hub)).catch(() => {});
    http.get("/nature/observations", { params: { category: "plants", limit: 8 } })
      .then(({ data }) => setRecent(data.observations || data.items || []))
      .catch(() => {});
    http.get("/docket-art/plantpulse").then(({ data }) => setArt(data.art || {})).catch(() => {});
  }, []);

  const onPickPhoto = (files) => {
    const f = files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => {
      try { sessionStorage.setItem("np_identify_prefill", r.result); } catch {}
      navigate("/identify");
    };
    r.readAsDataURL(f);
  };

  const findNearMe = () => {
    if (!navigator.geolocation) { setLocState("denied"); return; }
    setLocState("locating");
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        try {
          const { data } = await http.get("/nature/observations/nearby", {
            params: { lat: p.coords.latitude, lon: p.coords.longitude, radius_km: 100, limit: 12, category: "plants" },
          });
          setNearby(data.observations || []);
        } catch { setNearby([]); }
        setLocState("idle");
      },
      () => setLocState("denied"),
      { timeout: 10000 },
    );
  };

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const obsCards = nearby ?? recent;

  return (
    <main className="min-h-screen bg-white text-[#14532D]" data-testid="plantpulse-home">
      <SeoMeta
        title="Plant Pulse — a living global atlas of plant life"
        description="Photograph a plant and discover what it is. Plant Pulse identifies, documents and maps plant life — trees, wildflowers, grasses, ferns and more — into a decades-long record of changing vegetation."
        canonical={`${origin}/plants`}
        jsonLd={{ "@context": "https://schema.org", "@type": "CollectionPage", name: "Plant Pulse", url: `${origin}/plants` }}
      />

      {/* ---------------- HERO ---------------- */}
      <section className="relative overflow-hidden pt-32 md:pt-36" data-testid="plantpulse-hero">
        {art.hero && (
          <div className="absolute inset-0">
            <img src={abs(art.hero)} alt="Forest lake with coneflowers and a monarch butterfly" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/15" />
          </div>
        )}
        <div className="relative max-w-[1200px] mx-auto px-5 md:px-10 pb-14">
          <div className="grid gap-10 lg:grid-cols-[1.25fr_1fr] items-center">
            <div>
              <h1 className="font-serif-display text-5xl md:text-7xl leading-[0.98] tracking-tight" data-testid="plantpulse-title">
                <span className="text-[#65A30D]">Plant</span>
                <Leaf size={34} weight="fill" className="inline-block text-[#166534] -mt-8 mx-1" aria-hidden />
                <span className="text-[#166534]">Pulse</span>
              </h1>
              <div className="mt-2 font-mono-tech text-[11px] tracking-[0.3em] uppercase text-[#3F6212]" data-testid="plantpulse-tagline">
                Discover · Identify · Document · Protect
              </div>
              <p className="mt-4 max-w-xl text-base md:text-lg text-[#1F2937] leading-relaxed" data-testid="plantpulse-intro">
                From towering trees to tiny wildflowers, plants sustain life on Earth. Learn about them,
                identify them, and join a global community documenting the green world around us.
              </p>
              <ListenTranslateBar text="Plant Pulse. From towering trees to tiny wildflowers — discover, identify, document and protect the plant life around you." testidPrefix="plantpulse-tools" />
              <div className="mt-6"><PageGuide pageKey="plants:home" testidPrefix="plantpulse" /></div>
              <div className="mt-6">
                <Link to="/explore/flora" data-testid="plantpulse-explore-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-[#166534] px-7 py-3.5 font-mono-tech text-[11px] tracking-[0.26em] uppercase text-white hover:bg-[#14532D] transition-colors">
                  Explore Plants <ArrowRight size={14} weight="bold" />
                </Link>
              </div>
            </div>

            {/* Upload card */}
            <div className="rounded-3xl bg-white/95 backdrop-blur border border-black/5 shadow-xl p-6" data-testid="plantpulse-upload-card">
              <h2 className="font-serif-display text-2xl text-[#14532D]">What plant is this?</h2>
              <p className="mt-1 text-sm text-[#374151]">Upload a photo and let AI help you identify it.</p>
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); onPickPhoto(e.dataTransfer.files); }}
                className="mt-4 cursor-pointer rounded-2xl border-2 border-dashed border-[#166534]/25 hover:border-[#166534]/60 transition-colors p-8 text-center"
                data-testid="plantpulse-dropzone"
              >
                <Camera size={30} weight="bold" className="mx-auto text-[#166534]" />
                <div className="mt-2 font-medium text-[#14532D]">Upload a Photo</div>
                <div className="mt-0.5 text-xs text-[#6B7280]">Drag &amp; drop or click to choose</div>
              </div>
              <input ref={fileRef} type="file" accept="image/*" hidden data-testid="plantpulse-file-input"
                onChange={(e) => { onPickPhoto(e.target.files); e.target.value = ""; }} />
              <button onClick={() => fileRef.current?.click()} data-testid="plantpulse-identify-cta"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#166534] to-[#65A30D] px-6 py-3 font-medium text-white hover:opacity-95">
                Identify Plant <Sparkle size={15} weight="fill" />
              </button>
              <Link to="/identify" className="mt-3 block text-center text-sm text-[#166534] hover:underline" data-testid="plantpulse-how-it-works">
                Learn about Plant Identification ›
              </Link>
            </div>
          </div>

          {/* Handwritten accent + Muir quote */}
          <div className="hidden lg:block absolute right-2 top-40 font-serif-display italic text-[#3F6212] text-lg leading-tight [writing-mode:vertical-rl]">
            Plants · People · Healthy Planet
          </div>
          <blockquote className="mt-10 max-w-sm ml-auto text-right" data-testid="plantpulse-quote">
            <p className="font-serif-display italic text-lg text-[#1F2937] leading-snug">
              "In every walk with nature one receives far more than they seek."
            </p>
            <footer className="mt-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#4B5563]">— John Muir</footer>
          </blockquote>
        </div>
      </section>

      {/* ---------------- CATEGORY CIRCLES ---------------- */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 mt-8" data-testid="plantpulse-categories">
        <div className="flex flex-wrap items-start justify-center gap-x-5 gap-y-6">
          {CATEGORIES.map((c) => (
            <div key={c.label} className="w-24 text-center">
              <Link to={c.to} data-testid={`plantpulse-cat-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="group block">
                {art[c.art] ? (
                  <img src={abs(art[c.art])} alt={c.label} loading="lazy"
                    className="mx-auto h-20 w-20 rounded-full object-cover border-2 border-[#166534]/15 shadow-sm group-hover:border-[#166534]/50 group-hover:scale-105 transition-all" />
                ) : (
                  <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#F0FDF4] border-2 border-[#166534]/15 shadow-sm">
                    <Plant size={28} weight="fill" className="text-[#166534]" />
                  </span>
                )}
                <div className="mt-2 text-sm font-medium text-[#14532D] group-hover:text-[#166534] leading-tight">{c.label}</div>
              </Link>
              <Link to={`/read/articles/${c.guide}`} data-testid={`plantpulse-cat-guide-${c.guide}`}
                className="mt-0.5 inline-block font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#B85F2A] hover:text-[#a25526]">
                AI guide →
              </Link>
            </div>
          ))}
          <Link to="/explore" data-testid="plantpulse-cat-more" className="group w-24 text-center">
            <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#65A30D] text-white text-2xl font-bold group-hover:bg-[#166534] transition-colors">···</span>
            <div className="mt-2 text-sm font-medium text-[#14532D] group-hover:text-[#166534]">More Categories</div>
          </Link>
        </div>
      </section>

      {/* ---------------- 5 FEATURES ---------------- */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 mt-14" data-testid="plantpulse-features">
        <div className="grid gap-6 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
          {FEATURES.map((f) => (
            <Link key={f.title} to={`/read/articles/${f.guide}`} className="text-center group block" data-testid={`plantpulse-feature-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#F0FDF4] border border-[#166534]/15 group-hover:border-[#166534]/40 transition-colors">
                <f.icon size={22} weight="fill" className="text-[#166534]" />
              </span>
              <div className="mt-2 font-semibold text-[#14532D] group-hover:text-[#166534]">{f.title}</div>
              <p className="mt-1 text-[13px] text-[#4B5563] leading-snug">{f.copy}</p>
              <span className="mt-1.5 inline-block font-mono-tech text-[9px] tracking-[0.18em] uppercase text-[#B85F2A]">Read the AI guide →</span>
            </Link>
          ))}
        </div>
      </section>

      {/* ---------------- NEAR YOU / RECENT ---------------- */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 mt-14" data-testid="plantpulse-observations">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="font-serif-display text-2xl md:text-3xl text-[#14532D]">
            {nearby ? "Plants near you" : "Recent plant observations"}
          </h2>
          <div className="flex items-center gap-3">
            {flora && (
              <span className="font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[#6B7280]">
                {flora.entity_count} profiles · {flora.observation_count} observations
              </span>
            )}
            <button onClick={findNearMe} disabled={locState === "locating"} data-testid="plantpulse-near-me-btn"
              className="inline-flex items-center gap-2 rounded-full border border-[#166534]/30 px-4 py-2 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[#166534] hover:bg-[#166534] hover:text-white transition-colors disabled:opacity-50">
              <MapPin size={13} weight="bold" /> {locState === "locating" ? "Locating…" : nearby ? "Refresh" : "Use my location"}
            </button>
          </div>
        </div>
        {locState === "denied" && (
          <p className="mt-2 text-sm text-[#B45309]" data-testid="plantpulse-near-me-denied">
            Location unavailable — showing recent observations instead.
          </p>
        )}
        {obsCards.length === 0 ? (
          <p className="mt-4 text-sm text-[#4B5563]" data-testid="plantpulse-obs-empty">
            No plant observations {nearby ? "within 100 km yet — yours could be the first" : "yet"}. Photograph a plant and identify it to start the record.
          </p>
        ) : (
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {obsCards.slice(0, 8).map((o) => (
              <Link key={o.id} to={`/observations/${o.id}`} data-testid={`plantpulse-obs-${o.id}`}
                className="rounded-2xl border border-black/5 bg-white overflow-hidden shadow-sm hover:border-[#166534]/40 transition-colors">
                {o.media?.[0]?.url && (
                  <img src={abs(o.media[0].url)} alt={o.title} className="h-28 w-full object-cover" loading="lazy" />
                )}
                <div className="p-3">
                  <div className="font-medium text-sm text-[#14532D] leading-tight">{o.title || "Plant observation"}</div>
                  <div className="mt-1 font-mono-tech text-[9px] tracking-[0.16em] uppercase text-[#6B7280]">
                    {o.date}{o.location?.city || o.location?.display_name ? ` · ${o.location.city || o.location.display_name}` : ""}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* ---------------- TRUST LADDER ---------------- */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 mt-14" data-testid="plantpulse-trust">
        <div className="rounded-3xl bg-[#E9F3EA] border border-[#1B4332]/10 text-[#1B4332] p-6 md:p-8">
          <div className="flex items-center gap-2 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#166534]">
            <SealCheck size={14} weight="fill" /> How identification earns trust
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-5">
            {LADDER.map(([label, desc]) => (
              <div key={label} className="rounded-xl bg-white border border-black/8 p-3 shadow-sm">
                <div className="font-serif-display text-base text-[#14532D]">{label}</div>
                <div className="mt-1 text-[12px] text-[#5B4A38] leading-snug">{desc}</div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-[12px] text-[#4A3B2C]">
            NaturePulse Identify combines visual AI with open taxonomic databases (iNaturalist today;
            Pl@ntNet and botanical databases join as their keys arrive). Safety labels like edible or
            poisonous never come from an image guess — and a wild plant is never declared safe to eat.
          </p>
          <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-[12px] text-[#4A3B2C]">
            <span className="inline-flex items-center gap-1.5">
              <Tree size={14} weight="fill" className="text-[#166534]" />
              One observation = one permanent data point in a decades-long record
            </span>
            <span className="inline-flex items-center gap-1.5">
              <GlobeHemisphereWest size={14} weight="fill" className="text-[#166534]" />
              Observations roll up from neighbourhood to planet
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Flower size={14} weight="fill" className="text-[#166534]" />
              Fungi have their own hub — same honesty rules apply
            </span>
          </div>
        </div>
      </section>

      {/* ---------------- CONTRIBUTE CTA ---------------- */}
      <section className="max-w-[1200px] mx-auto px-5 md:px-10 mt-12 pb-10" data-testid="plantpulse-contribute">
        <div className="rounded-3xl border border-black/5 bg-white p-6 shadow-sm flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <HandHeart size={26} weight="fill" className="text-[#166534]" />
            <div>
              <div className="font-serif-display text-2xl text-[#14532D]">Grow the atlas</div>
              <p className="text-sm text-[#4B5563]">Every plant you identify becomes part of a living global record.</p>
            </div>
          </div>
          <Link to="/identify" data-testid="plantpulse-contribute-cta"
            className="inline-flex items-center gap-2 rounded-full bg-[#166534] px-6 py-3 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-white hover:bg-[#14532D] transition-colors">
            Identify a plant <ArrowRight size={12} weight="bold" />
          </Link>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-5 md:px-10 mt-10">
        <RelatedReading tag="plant-pulse" testid="plantpulse-related" />
      </section>

      {/* ---------------- FOOTER BAND (mockup) ---------------- */}
      <section className="bg-[#14532D] text-[#E9F3EA]" data-testid="plantpulse-footer-band">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="font-serif-display text-lg">
            NaturePulse <span className="mx-2 opacity-40">|</span>
            <span className="font-serif-display italic">Observing Today · Preserving Tomorrow</span>
          </div>
          <div className="font-mono-tech text-[10px] tracking-[0.2em] uppercase flex flex-wrap gap-x-6 gap-y-1 text-[#E9F3EA]/85">
            <span>Plants Power Life</span>
            <span>People Make a Difference</span>
            <span>A Healthier Planet is Possible</span>
          </div>
        </div>
      </section>
    </main>
  );
}
